import React, { useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Divider,
  Label,
  LabelGroup,
  MenuToggle,
  MenuToggleCheckbox,
  MenuToggleElement,
  Popover,
  Select,
  SelectList,
  SelectOption,
  TextInputGroup,
  TextInputGroupMain,
  TextInputGroupUtilities,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  ToolbarToggleGroup,
} from '@patternfly/react-core';
import { CogIcon, CompressIcon, ExpandIcon, ExportIcon, FilterIcon, TimesIcon } from '@patternfly/react-icons';
import { DateRange, Project, ReportGroupingConfig, ReportViewType, VIEW_TYPE_LABELS } from '../../types/reports';
import { User } from '../../types';
import { ReportsGroupingConfig } from './ReportsGroupingConfig';
import { UserSelect } from '../common';

interface ReportsToolbarProps {
  dateRange: DateRange;
  groupingConfig: ReportGroupingConfig;
  viewType: ReportViewType;
  availableUsers: User[];
  availableProjects: Project[];
  availableLabels: string[];
  selectedUsers: string[];
  selectedProjects: string[];
  selectedLabels: string[];
  isAdmin: boolean;
  loading: boolean;
  isFullscreen: boolean;
  onDateRangeChange: (range: DateRange) => void;
  onGroupingChange: (config: ReportGroupingConfig) => void;
  onViewTypeChange: (viewType: ReportViewType) => void;
  onUsersChange: (userIds: string[]) => void;
  onProjectsChange: (projectIds: string[]) => void;
  onLabelsChange: (labels: string[]) => void;
  onRefresh: () => void;
  onToggleFullscreen: () => void;
  onExport: () => void;
}

export const ReportsToolbar: React.FC<ReportsToolbarProps> = ({
  dateRange,
  groupingConfig,
  viewType,
  availableUsers,
  availableProjects,
  availableLabels,
  selectedUsers,
  selectedProjects,
  selectedLabels,
  isAdmin,
  loading,
  isFullscreen,
  onDateRangeChange,
  onGroupingChange,
  onViewTypeChange,
  onUsersChange,
  onProjectsChange,
  onLabelsChange,
  onRefresh,
  onToggleFullscreen,
  onExport,
}) => {
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const [isLabelsOpen, setIsLabelsOpen] = useState(false);
  const [isGroupingOpen, setIsGroupingOpen] = useState(false);
  const [isViewTypeOpen, setIsViewTypeOpen] = useState(false);
  const [projectSearchValue, setProjectSearchValue] = useState('');
  const [labelSearchValue, setLabelSearchValue] = useState('');

  // Helper functions
  const getUserName = (userId: string): string => {
    const user = availableUsers.find((u) => u.id === userId);
    return user?.displayName || userId;
  };

  const getProjectName = (projectId: string): string => {
    const project = availableProjects.find((p) => p.id === projectId);
    if (!project) return projectId;
    const name = project.displayName || project.name || project.key;
    return project.key ? `${name} (${project.key})` : name;
  };

  const handleRemoveUser = (userId: string) => {
    onUsersChange(selectedUsers.filter((id) => id !== userId));
  };

  const handleRemoveProject = (projectId: string) => {
    onProjectsChange(selectedProjects.filter((id) => id !== projectId));
  };

  const handleRemoveLabel = (label: string) => {
    onLabelsChange(selectedLabels.filter((l) => l !== label));
  };

  const handleClearAllFilters = () => {
    onUsersChange([]);
    onProjectsChange([]);
    onLabelsChange([]);
  };

  const hasActiveFilters = selectedUsers.length > 0 || selectedProjects.length > 0 || selectedLabels.length > 0;

  const handleStartDateChange = (_event: React.FormEvent<HTMLInputElement>, value: string) => {
    if (value) {
      onDateRangeChange({ ...dateRange, startDate: value });
    }
  };

  const handleEndDateChange = (_event: React.FormEvent<HTMLInputElement>, value: string) => {
    if (value) {
      onDateRangeChange({ ...dateRange, endDate: value });
    }
  };

  const handleProjectSelect = (_event: React.MouseEvent | undefined, value: string | number | undefined) => {
    if (value === undefined) return;
    const projectId = String(value);

    // Handle select all / deselect all
    if (projectId === '__select_all__') {
      onProjectsChange(availableProjects.map((p) => p.id));
      return;
    }
    if (projectId === '__deselect_all__') {
      onProjectsChange([]);
      return;
    }

    const newSelected = selectedProjects.includes(projectId)
      ? selectedProjects.filter((id) => id !== projectId)
      : [...selectedProjects, projectId];
    onProjectsChange(newSelected);
  };

  // Filtered lists for search
  const filteredProjects = useMemo(() => {
    if (!projectSearchValue) return availableProjects;
    const searchLower = projectSearchValue.toLowerCase();
    return availableProjects.filter((project) => {
      const name = project.displayName || project.name || project.key || '';
      return name.toLowerCase().includes(searchLower);
    });
  }, [availableProjects, projectSearchValue]);

  // Checkbox states for projects
  const allProjectsSelected = selectedProjects.length === availableProjects.length && availableProjects.length > 0;
  const noProjectsSelected = selectedProjects.length === 0;
  const projectsCheckboxState: boolean | null = noProjectsSelected ? false : allProjectsSelected ? true : null;

  const handleProjectsCheckboxClick = () => {
    // If empty or indeterminate -> select all; if checked -> deselect all
    if (projectsCheckboxState === true) {
      onProjectsChange([]);
    } else {
      onProjectsChange(availableProjects.map((p) => p.id));
    }
  };

  const projectsToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      variant="typeahead"
      onClick={() => setIsProjectsOpen(!isProjectsOpen)}
      isExpanded={isProjectsOpen}
      isFullWidth
      className="reports-toolbar__projects-toggle"
      splitButtonItems={[
        <MenuToggleCheckbox
          key="projects-checkbox"
          id="projects-select-checkbox"
          isChecked={projectsCheckboxState}
          onChange={handleProjectsCheckboxClick}
          aria-label="Select all projects"
        />,
      ]}
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={projectSearchValue}
          onClick={() => setIsProjectsOpen(true)}
          onChange={(_event, value) => setProjectSearchValue(value)}
          autoComplete="off"
          placeholder={selectedProjects.length > 0 ? `${selectedProjects.length} selected` : 'Filter projects...'}
          aria-label="Filter projects"
        />
        <TextInputGroupUtilities>
          {projectSearchValue && (
            <Button
              variant="plain"
              onClick={() => setProjectSearchValue('')}
              aria-label="Clear filter"
              style={{ padding: '0 4px' }}
            >
              <TimesIcon />
            </Button>
          )}
        </TextInputGroupUtilities>
      </TextInputGroup>
    </MenuToggle>
  );

  const handleProjectsOpenChange = (open: boolean) => {
    setIsProjectsOpen(open);
    if (!open) {
      setProjectSearchValue('');
    }
  };

  // Labels filter
  const handleLabelSelect = (_event: React.MouseEvent | undefined, value: string | number | undefined) => {
    if (value === undefined) return;
    const label = String(value);

    if (label === '__select_all__') {
      onLabelsChange([...availableLabels]);
      return;
    }
    if (label === '__deselect_all__') {
      onLabelsChange([]);
      return;
    }

    const newSelected = selectedLabels.includes(label)
      ? selectedLabels.filter((l) => l !== label)
      : [...selectedLabels, label];
    onLabelsChange(newSelected);
  };

  const filteredLabels = useMemo(() => {
    if (!labelSearchValue) return availableLabels;
    const searchLower = labelSearchValue.toLowerCase();
    return availableLabels.filter((label) => label.toLowerCase().includes(searchLower));
  }, [availableLabels, labelSearchValue]);

  const allLabelsSelected = selectedLabels.length === availableLabels.length && availableLabels.length > 0;
  const noLabelsSelected = selectedLabels.length === 0;
  const labelsCheckboxState: boolean | null = noLabelsSelected ? false : allLabelsSelected ? true : null;

  const handleLabelsCheckboxClick = () => {
    if (labelsCheckboxState === true) {
      onLabelsChange([]);
    } else {
      onLabelsChange([...availableLabels]);
    }
  };

  const labelsToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      variant="typeahead"
      onClick={() => setIsLabelsOpen(!isLabelsOpen)}
      isExpanded={isLabelsOpen}
      isFullWidth
      className="reports-toolbar__labels-toggle"
      splitButtonItems={[
        <MenuToggleCheckbox
          key="labels-checkbox"
          id="labels-select-checkbox"
          isChecked={labelsCheckboxState}
          onChange={handleLabelsCheckboxClick}
          aria-label="Select all labels"
        />,
      ]}
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={labelSearchValue}
          onClick={() => setIsLabelsOpen(true)}
          onChange={(_event, value) => setLabelSearchValue(value)}
          autoComplete="off"
          placeholder={selectedLabels.length > 0 ? `${selectedLabels.length} selected` : 'Filter labels...'}
          aria-label="Filter labels"
        />
        <TextInputGroupUtilities>
          {labelSearchValue && (
            <Button
              variant="plain"
              onClick={() => setLabelSearchValue('')}
              aria-label="Clear filter"
              style={{ padding: '0 4px' }}
            >
              <TimesIcon />
            </Button>
          )}
        </TextInputGroupUtilities>
      </TextInputGroup>
    </MenuToggle>
  );

  const handleLabelsOpenChange = (open: boolean) => {
    setIsLabelsOpen(open);
    if (!open) {
      setLabelSearchValue('');
    }
  };

  return (
    <Toolbar className="reports-toolbar">
      <ToolbarContent>
        <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="lg">
          <ToolbarGroup variant="filter-group">
            <ToolbarItem>
              <DatePicker
                value={dateRange.startDate}
                onChange={handleStartDateChange}
                aria-label="Start date"
                placeholder="Start date"
              />
            </ToolbarItem>
            <ToolbarItem>
              <span className="reports-toolbar__date-separator">to</span>
            </ToolbarItem>
            <ToolbarItem>
              <DatePicker
                value={dateRange.endDate}
                onChange={handleEndDateChange}
                aria-label="End date"
                placeholder="End date"
              />
            </ToolbarItem>
          </ToolbarGroup>

          {isAdmin && availableUsers.length > 0 && (
            <ToolbarItem>
              <UserSelect
                availableUsers={availableUsers}
                selectedUsers={selectedUsers}
                onSelectionChange={onUsersChange}
                placeholder="Filter users..."
                isMultiSelect
              />
            </ToolbarItem>
          )}

          {availableProjects.length > 0 && (
            <ToolbarItem>
              <Select
                role="menu"
                toggle={projectsToggle}
                onSelect={handleProjectSelect}
                selected={selectedProjects}
                isOpen={isProjectsOpen}
                onOpenChange={handleProjectsOpenChange}
              >
                <SelectList>
                  <SelectOption
                    key="__select_all__"
                    value="__select_all__"
                    isDisabled={allProjectsSelected}
                  >
                    Select all
                  </SelectOption>
                  <SelectOption
                    key="__deselect_all__"
                    value="__deselect_all__"
                    isDisabled={noProjectsSelected}
                  >
                    Deselect all
                  </SelectOption>
                  <Divider />
                  {filteredProjects.length === 0 ? (
                    <SelectOption isDisabled>No projects found</SelectOption>
                  ) : (
                    filteredProjects.map((project) => {
                      const name = project.displayName || project.name || project.key;
                      const displayText = project.key ? `${name} (${project.key})` : name;
                      return (
                        <SelectOption
                          key={project.id}
                          value={project.id}
                          hasCheckbox
                          isSelected={selectedProjects.includes(project.id)}
                        >
                          {displayText}
                        </SelectOption>
                      );
                    })
                  )}
                </SelectList>
              </Select>
            </ToolbarItem>
          )}

          <ToolbarItem>
            <Select
              role="menu"
              toggle={labelsToggle}
              onSelect={handleLabelSelect}
              selected={selectedLabels}
              isOpen={isLabelsOpen}
              onOpenChange={handleLabelsOpenChange}
            >
              <SelectList>
                {availableLabels.length > 0 && (
                  <>
                    <SelectOption
                      key="__select_all__"
                      value="__select_all__"
                      isDisabled={allLabelsSelected}
                    >
                      Select all
                    </SelectOption>
                    <SelectOption
                      key="__deselect_all__"
                      value="__deselect_all__"
                      isDisabled={noLabelsSelected}
                    >
                      Deselect all
                    </SelectOption>
                    <Divider />
                  </>
                )}
                {filteredLabels.length === 0 ? (
                  <SelectOption isDisabled>No labels found</SelectOption>
                ) : (
                  filteredLabels.map((label) => (
                    <SelectOption
                      key={label}
                      value={label}
                      hasCheckbox
                      isSelected={selectedLabels.includes(label)}
                    >
                      {label}
                    </SelectOption>
                  ))
                )}
              </SelectList>
            </Select>
          </ToolbarItem>

          <ToolbarItem>
            <Popover
              aria-label="Grouping configuration"
              hasAutoWidth
              showClose
              isVisible={isGroupingOpen}
              shouldClose={() => setIsGroupingOpen(false)}
              bodyContent={
                <ReportsGroupingConfig config={groupingConfig} onChange={onGroupingChange} />
              }
            >
              <MenuToggle
                icon={<CogIcon />}
                onClick={() => setIsGroupingOpen(!isGroupingOpen)}
                isExpanded={isGroupingOpen}
                className="reports-toolbar__grouping-toggle"
              >
                Group by
              </MenuToggle>
            </Popover>
          </ToolbarItem>

          {/* ViewType ToggleGroup - visible on desktop */}
          <ToolbarItem className="reports-toolbar__view-toggle-group">
            <ToggleGroup aria-label="View type">
              {(['daily', 'weekly', 'monthly'] as ReportViewType[]).map((type) => (
                <ToggleGroupItem
                  key={type}
                  text={VIEW_TYPE_LABELS[type]}
                  buttonId={`view-type-${type}`}
                  isSelected={viewType === type}
                  onChange={() => onViewTypeChange(type)}
                />
              ))}
            </ToggleGroup>
          </ToolbarItem>

          {/* ViewType Select - visible on smaller screens */}
          <ToolbarItem className="reports-toolbar__view-select">
            <Select
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsViewTypeOpen(!isViewTypeOpen)}
                  isExpanded={isViewTypeOpen}
                >
                  {VIEW_TYPE_LABELS[viewType]}
                </MenuToggle>
              )}
              onSelect={(_event, value) => {
                onViewTypeChange(value as ReportViewType);
                setIsViewTypeOpen(false);
              }}
              selected={viewType}
              isOpen={isViewTypeOpen}
              onOpenChange={setIsViewTypeOpen}
            >
              <SelectList>
                {(['daily', 'weekly', 'monthly'] as ReportViewType[]).map((type) => (
                  <SelectOption key={type} value={type}>
                    {VIEW_TYPE_LABELS[type]}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </ToolbarItem>
        </ToolbarToggleGroup>

        <ToolbarItem align={{ default: 'alignEnd' }}>
          <Button
            variant="plain"
            icon={<ExportIcon />}
            onClick={onExport}
            aria-label="Export to Excel"
          />
        </ToolbarItem>
        <ToolbarItem>
          <Button
            variant="plain"
            icon={isFullscreen ? <CompressIcon /> : <ExpandIcon />}
            onClick={onToggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          />
        </ToolbarItem>
      </ToolbarContent>

      {hasActiveFilters && (
        <ToolbarContent className="reports-toolbar__active-filters">
          <ToolbarItem>
            <LabelGroup categoryName="Active filters" numLabels={10}>
              {selectedUsers.map((userId) => (
                <Label key={userId} onClose={() => handleRemoveUser(userId)}>
                  {getUserName(userId)}
                </Label>
              ))}
              {selectedProjects.map((projectId) => (
                <Label key={projectId} onClose={() => handleRemoveProject(projectId)}>
                  {getProjectName(projectId)}
                </Label>
              ))}
              {selectedLabels.map((label) => (
                <Label key={`label-${label}`} color="blue" onClose={() => handleRemoveLabel(label)}>
                  {label}
                </Label>
              ))}
            </LabelGroup>
          </ToolbarItem>
          <ToolbarItem>
            <Button variant="link" onClick={handleClearAllFilters}>
              Clear all filters
            </Button>
          </ToolbarItem>
        </ToolbarContent>
      )}
    </Toolbar>
  );
};
